import { type ReactElement, useState } from 'react';
import { useFontsQuery, useInvalidateFonts } from '@/entities/font';
import {
  FontRow,
  FontUploadModal,
  useCreateFont,
  useDeleteFont,
  useRenameFont,
} from '@/features/fonts';
import { type RenameTarget, resetSettledRequests } from '@/shared/lib';
import { Button, ContentCard, cn, EmptyState, tableStyles } from '@/shared/ui';

/** 삭제 영향 — 적용 상품 수를 목록에 싣지 않으므로(결정 F4) 확인창이 영향을 알리는 유일한 자리다. */
const DELETE_IMPACT =
  '이 폰트를 허용한 모든 상품에서 빠집니다. 기존 디자인의 글자 모양은 그대로지만, 이 폰트를 쓴 디자인은 다른 폰트로 바꿔야 다시 저장할 수 있습니다. 같은 파일을 다시 올리면 새 폰트로 등록됩니다.';

/**
 * 폰트 마스터 — 고객 에디터의 글자 폰트 목록(이름·형식·관리). 인쇄 방식 마스터와 같은 구조다.
 * 이름 변경·삭제는 모든 상품의 허용 목록에 보이므로 상품별 허용 목록 캐시까지 무효화한다.
 */
export const FontMasterSection = (): ReactElement => {
  const invalidate = useInvalidateFonts();
  const fontsQuery = useFontsQuery();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);

  const createAction = useCreateFont({ onSuccess: invalidate.list });
  const renameAction = useRenameFont({
    onSuccess: () => {
      invalidate.afterMasterChange();
      setRenameTarget(null);
    },
  });
  const deleteAction = useDeleteFont({ onSuccess: invalidate.afterMasterChange });

  const mutationError = renameAction.error ?? deleteAction.error;

  // 이름 수정·삭제는 오류 한 줄(mutationError)을 함께 쓴다(추가 오류는 업로드 모달이 보여 준다). 새 요청을
  // 보내기 전에 두 요청의 지난 오류를 모두 지운다. 진행 중인 요청은 지우지 않는다 — 인쇄 방식 마스터와 같은 이유.
  const clearMutationErrors = (): void => {
    resetSettledRequests([renameAction, deleteAction]);
  };

  // 모달을 열고 닫을 때 지난 업로드·등록 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeAddModal = (isOpen: boolean): void => {
    resetSettledRequests([createAction]);
    setIsAddModalOpen(isOpen);
  };

  const handleCreate = async (input: {
    readonly file: File;
    readonly familyName: string;
  }): Promise<void> => {
    if (await createAction.createFont(input)) {
      changeAddModal(false);
    }
  };

  const rename = {
    target: renameTarget
      ? { ...renameTarget, isPending: renameAction.pendingId === renameTarget.id }
      : null,
    onChange: (name: string) =>
      setRenameTarget((current) => (current ? { ...current, name } : current)),
    onSubmit: () => {
      if (renameTarget?.name.trim()) {
        clearMutationErrors();
        renameAction.renameFont({ id: renameTarget.id, familyName: renameTarget.name.trim() });
      }
    },
    onCancel: () => setRenameTarget(null),
  };

  const handleDelete = (font: { readonly id: string; readonly familyName: string }): void => {
    if (window.confirm(`폰트 "${font.familyName}" 을(를) 삭제하시겠습니까?\n\n${DELETE_IMPACT}`)) {
      clearMutationErrors();
      deleteAction.deleteFont({ id: font.id });
    }
  };

  return (
    <ContentCard
      title="폰트"
      description="고객 에디터에서 글자에 쓰는 폰트입니다. 상품마다 허용할 폰트는 상품 편집에서 고릅니다."
      actions={
        <Button variant="outline" size="sm" onClick={() => changeAddModal(true)}>
          + 폰트 추가
        </Button>
      }
    >
      {mutationError ? (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {mutationError.message}
        </p>
      ) : null}

      {fontsQuery.isPending ? (
        <EmptyState>불러오는 중…</EmptyState>
      ) : fontsQuery.isError ? (
        <EmptyState isError>폰트를 불러오지 못했습니다: {fontsQuery.error.message}</EmptyState>
      ) : fontsQuery.data.length === 0 ? (
        <EmptyState>
          등록된 폰트가 없습니다. 폰트를 추가해야 상품에서 허용할 수 있습니다.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
          <table className={tableStyles.table}>
            <thead className={tableStyles.head}>
              <tr>
                <th className={tableStyles.th}>폰트 이름</th>
                <th className={cn(tableStyles.th, 'w-40')}>형식</th>
                <th className={cn(tableStyles.th, 'w-48')}>관리</th>
              </tr>
            </thead>
            <tbody>
              {fontsQuery.data.map((font) => (
                <FontRow
                  key={font.id}
                  font={font}
                  rename={rename}
                  onRenameStart={(target) =>
                    setRenameTarget({ id: target.id, name: target.familyName })
                  }
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        ⓘ 파일·형식은 등록한 뒤 바꿀 수 없습니다 — 이름만 수정할 수 있습니다.
      </p>

      {isAddModalOpen ? (
        <FontUploadModal
          phase={createAction.phase}
          errorMessage={createAction.error}
          nameErrorMessage={createAction.nameError}
          onSubmit={(input) => void handleCreate(input)}
          onClose={() => changeAddModal(false)}
        />
      ) : null}
    </ContentCard>
  );
};
